import { CartUpdateEvent } from '@theme/events';

/**
 * Keeps the header cart count and every rendered cart (drawer, cart page) in line with the
 * Shopify cart when a page is shown from the browser's history cache.
 *
 * The cart markup is rendered server-side with the page. Going Back/Forward (e.g. PDP → back to
 * the collection) shows that page as it was when it was first loaded — restored from the
 * back/forward cache (`pageshow` with `persisted`), or re-created from the HTTP cache (navigation
 * type `back_forward`) — so its mini-cart and count can predate items added on another page.
 *
 * On those navigations the rendered cart is compared with `/cart.js`; only if they differ are the
 * cart sections re-rendered and a regular CartUpdateEvent dispatched, so the drawer, cart page,
 * cart icon and product forms all update from the same Shopify cart response through their
 * existing listeners.
 */

export const CART_STATE_SYNC_SOURCE = 'cart-state-sync';

/** @type {Promise<void> | null} */
let pendingSync = null;

/**
 * @param {Array<{ key: string, quantity: number }>} items
 * @returns {string}
 */
function cartSignature(items) {
  return items.map((item) => `${item.key}:${item.quantity}`).join('|');
}

/**
 * The line items a cart-items-component currently shows.
 * @param {Element} component
 * @returns {string}
 */
function renderedSignature(component) {
  const rows = component.querySelectorAll('[data-key]');
  const items = Array.from(rows, (row) => {
    const input = row.querySelector('input[name="updates[]"]');
    const quantity = input instanceof HTMLInputElement ? Number(input.value) : NaN;
    return { key: row.getAttribute('data-key') ?? '', quantity };
  });
  return cartSignature(items);
}

/**
 * Whether the cart icon already shows `itemCount`.
 * @param {number} itemCount
 * @returns {boolean}
 */
function countIsCurrent(itemCount) {
  const count = document.querySelector('cart-icon [ref="cartBubbleCount"]');
  if (!count) return true;
  if (count.classList.contains('hidden')) return itemCount === 0;
  // The icon leaves the text empty from 100 items (see cart-icon.js).
  const text = (count.textContent || '').trim();
  return text === '' ? itemCount >= 100 : parseInt(text, 10) === itemCount;
}

async function syncCartState() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
  if (!response.ok) return;
  const cart = await response.json();

  const signature = cartSignature(cart.items);
  const staleSectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .filter((component) => renderedSignature(component) !== signature)
    .map((component) => /** @type {HTMLElement} */ (component).dataset.sectionId);

  if (staleSectionIds.length === 0 && countIsCurrent(cart.item_count)) return;

  /** @type {Record<string, string>} */
  let sections = {};
  if (staleSectionIds.length > 0) {
    // Rendered at the current URL so a cart page's own template section resolves too.
    const url = new URL(window.location.href);
    url.searchParams.set('sections', staleSectionIds.join(','));
    const sectionsResponse = await fetch(url, { cache: 'no-store' });
    if (sectionsResponse.ok) sections = await sectionsResponse.json();
  }

  // The listeners live in lazily loaded modules; make sure they are registered before dispatching.
  await Promise.all(
    ['cart-items-component', 'cart-icon']
      .filter((tag) => document.querySelector(tag))
      .map((tag) => customElements.whenDefined(tag))
  );

  document.dispatchEvent(
    new CartUpdateEvent(cart, CART_STATE_SYNC_SOURCE, {
      source: CART_STATE_SYNC_SOURCE,
      itemCount: cart.item_count,
      sections,
    })
  );
}

function requestSync() {
  pendingSync ??= syncCartState()
    .catch((error) => console.error('Cart state sync failed', error))
    .finally(() => {
      pendingSync = null;
    });
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) requestSync();
});

const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
  performance.getEntriesByType('navigation')[0]
);
if (navigation?.type === 'back_forward') requestSync();
